import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';

import CommonImage from '@components/CommonImage';
import ModalContainer from '@components/modals/ModalContainer';
import LoaderComponent from '@components/LoaderComponent';

import {
	setIsEditProject,
	setIsShowCreateProjectModal,
	setSelectedProjectToEdit
} from '@features/project-management-tools/redux/myProjectSlice';
import { useLazyGetProjectByIdQuery } from '@features/project-management-tools/api/projectManagementApi';

import { capitalizeFirstLetter, changeDateFormat } from '@utils/Util';

const ViewProjectDetailsModal = ({ onClose, projectId }) => {
	const [getProjectById, { data: projectInfo, isLoading }] = useLazyGetProjectByIdQuery();
	const dispatch = useDispatch();

	const handleEdit = () => {
		dispatch(setIsEditProject(true));
		dispatch(setIsShowCreateProjectModal(true));
		dispatch(setSelectedProjectToEdit(projectInfo));
	};

	useEffect(() => {
		getProjectById(projectId);
	}, [projectId]);

	return (
		<LoaderComponent loading={isLoading}>
			<ModalContainer className="events-view-modal">
				<div className="popup-wrapper">
					<div className="common-popup">
						<div className="common-popup-header d-flex justify-content-between border-0">
							<div
								className="popup-tilte d-flex align-items-center cursor-pointer"
								onClick={() => window.open(`/my-project/${projectId}`, '_blank')}
							>
								<h3 className="semibold-16 mb-0 text-underline gray_dark_01">
									<span className="cursor-pointer ">
										{projectInfo?.name || 'Project Details'}&nbsp;
									</span>
								</h3>
								<span className="ml-2">
									<CommonImage
										src="/icons/action-edit.png"
										width={16}
										height={16}
										alt="external"
									/>
								</span>
							</div>
							<div className="close-btn">
								<CommonImage alt="close" src="/icons/close.svg" onClick={onClose} />
							</div>
						</div>
						<div className="common-popup-body scollable-modal-body pb-0 scroll-y-label">
							<div className="single-overview-content pb-3">
								<ul>
									<li className="regular-14 gray_dark_01 weight-500 d-flex align-items-center mb-1">
										<p className="title gray_dark_02">Project type:</p>
										<p className="value">{projectInfo?.projectTypeName}</p>
									</li>
									<li className="regular-14 gray_dark_01 weight-500 d-flex align-items-center mb-1">
										<p className="title gray_dark_02">Brand:</p>
										<p className="value">{projectInfo?.brandResponse?.name}</p>
									</li>
									<li className="regular-14 gray_dark_01 weight-500 d-flex align-items-center">
										<p className="title gray_dark_02">Project status:</p>
										<div className="sort-button ml-0">
											<select
												name="status"
												className="border-0 bg-transparent py-0 gray_dark_01 pl-0"
												disabled={true}
											>
												<option value={projectInfo?.projectStatus}>
													{capitalizeFirstLetter(projectInfo?.projectStatus)}
												</option>
											</select>
										</div>
									</li>
									<li className="regular-14 gray_dark_01 weight-500 d-flex align-items-center mb-1">
										<p className="title gray_dark_02">Project progress:</p>
										<div className="progress-info task-progress-info mt-2 d-flex align-items-center justify-content-start">
											<div className="progress progress-square">
												<div
													className="progress-bar"
													style={{
														width: `${(projectInfo?.noOfCompletedTask / projectInfo?.noOfTotalTask) * 100}%`
													}}
												></div>
											</div>
											<div className="progress-text ml-2">
												<p className="regular-14 gray_dark_01">
													{projectInfo?.noOfCompletedTask}/
													{projectInfo?.noOfTotalTask}
												</p>
											</div>
										</div>
									</li>
									<li className="regular-14 gray_dark_01 weight-500 d-flex align-items-center mb-1">
										<p className="title gray_dark_02">Project owner:</p>
										<p className="value">{projectInfo?.ownerResponse?.name}</p>
									</li>
									<li className="regular-14 gray_dark_01 weight-500 d-flex align-items-center mb-1">
										<p className="title gray_dark_02">Creator:</p>
										<p className="value">{projectInfo?.createdByResponse?.name}</p>
									</li>
									<li className="regular-14 gray_dark_01 weight-500 d-flex align-items-center mb-1">
										<p className="title gray_dark_02">Project end date:</p>
										<p className="value">
											{changeDateFormat(projectInfo?.endDate, '', 'DD MMM, YYYY')}
										</p>
									</li>
								</ul>
							</div>
							<div className="single-overview-content border-top pt-3">
								<ul>
									<li className="regular-14 gray_dark_01 weight-500 d-flex align-items-center mb-1">
										<p className="title gray_dark_02">Buyer meeting date:</p>
										<p className="value">
											{changeDateFormat(
												projectInfo?.buyerFirstMeetingDate,
												'',
												'DD MMM, YYYY'
											)}
										</p>
									</li>
									{projectInfo?.ProjectType === 'BUYER_SIGN_OFF' && (
										<li className="regular-14 gray_dark_01 weight-500 d-flex align-items-center mb-1">
											<p className="title gray_dark_02">Buyer sign off date:</p>
											<p className="value">
												{changeDateFormat(
													projectInfo?.buyerSignOffDate,
													'',
													'DD MMM, YYYY'
												)}
											</p>
										</li>
									)}
									<li className="regular-14 gray_dark_01 weight-500 d-flex align-items-center mb-1">
										<p className="title gray_dark_02">Season:</p>
										<p className="value">{projectInfo?.season}</p>
									</li>
									<li className="regular-14 gray_dark_01 weight-500 d-flex align-items-center">
										<p className="title gray_dark_02">Style category:</p>
										<p className="value">{projectInfo?.styleCategoryDetails}</p>
									</li>
									<li className="regular-14 gray_dark_01 weight-500 d-flex align-items-center mb-1">
										<p className="title gray_dark_02">Fabric details:</p>
										<p className="value">{projectInfo?.fabricDetails}</p>
									</li>
									<li className="regular-14 gray_dark_01 weight-500 d-flex align-items-center mb-1">
										<p className="title gray_dark_02">Notes:</p>
										<p className="value">{projectInfo?.note}</p>
									</li>
								</ul>
							</div>
						</div>
						<div className="modal-footer pt-0 justify-content-start">
							<button className="button text size32 px-0 text-underline" onClick={handleEdit}>
								Edit
							</button>
						</div>
					</div>
				</div>
			</ModalContainer>
		</LoaderComponent>
	);
};

export default ViewProjectDetailsModal;
